import { z } from 'zod';
import { pageQuerySchema } from './marketplace.js';
import { currencyCodeSchema, minorAmountSchema } from './money.js';
/**
 * Payment methods and the money ledger.
 * [Parent steps 4 and 22, Job Poster step 28]
 *
 * Card data never reaches this system. A payment method is a token issued by a
 * payment provider plus a human label — "Visa 4242" — and nothing else, so
 * there is no card number here to leak, log, or accidentally return.
 */
export const paymentMethodTypeSchema = z.enum(['CARD', 'BANK', 'WALLET']);
export const paymentMethodSchema = z.object({
    id: z.string(),
    type: paymentMethodTypeSchema,
    /** Brand and last four, or a bank nickname. Never a full number. */
    label: z.string().nullable(),
    isDefault: z.boolean(),
    createdAt: z.string(),
});
/**
 * Adding a method.
 *
 * The token comes from the provider's own client-side widget, so the card
 * number goes from the browser straight to the provider and never touches this
 * api. A payload carrying anything that looks like a card number is rejected.
 */
export const addPaymentMethodInputSchema = z.object({
    type: paymentMethodTypeSchema,
    providerToken: z
        .string()
        .trim()
        .min(8, 'That does not look like a provider token')
        .max(255)
        .refine((value) => !/\d{13,19}/.test(value.replace(/[\s-]/g, '')), 'Send the provider token, not a card number'),
    label: z.string().trim().max(60).optional(),
});
/* -------------------------------------------------------------------------- */
/* Ledger                                                                     */
/* -------------------------------------------------------------------------- */
export const transactionTypeSchema = z.enum([
    'PAYMENT',
    'PAYOUT',
    'REFUND',
    'CHARGEBACK',
    'COMMISSION',
    'SUBSCRIPTION',
]);
export const transactionStatusSchema = z.enum(['PENDING', 'SUCCESS', 'FAILED', 'REVERSED']);
export const transactionSchema = z.object({
    id: z.string(),
    type: transactionTypeSchema,
    status: transactionStatusSchema,
    amountMinor: minorAmountSchema,
    currency: currencyCodeSchema,
    /** The job it paid for, when there is one. */
    jobTitle: z.string().nullable(),
    /** The other party, from the reader's point of view. */
    counterpartyName: z.string().nullable(),
    /** True when the reader paid, false when they were paid. */
    outgoing: z.boolean(),
    /** The provider's own reference, once settlement is real. */
    gatewayRef: z.string().nullable(),
    createdAt: z.string(),
});
export const transactionListQuerySchema = pageQuerySchema.extend({
    /** A guardian narrowing to one teen. Any publicId that names them. */
    teenId: z.string().uuid().optional(),
});
/** Totals for the money screens. */
export const moneySummarySchema = z.object({
    currency: currencyCodeSchema,
    /** Settled money in, over all time. */
    receivedMinor: minorAmountSchema,
    /** Settled money out. */
    paidMinor: minorAmountSchema,
    /** Agreed but not yet released. */
    pendingMinor: minorAmountSchema,
    /**
     * Whether a payment provider is actually connected.
     *
     * The screens say so plainly either way. A ledger shown without this reads
     * as money that moved, and on the manual gateway it did not.
     */
    settlesRealMoney: z.boolean(),
});
/* -------------------------------------------------------------------------- */
/* Insights — Parent step 23                                                  */
/* -------------------------------------------------------------------------- */
/** One month of a teen's activity. */
export const monthlyActivitySchema = z.object({
    /** "2026-09". */
    month: z.string(),
    earnedMinor: minorAmountSchema,
    jobsCompleted: z.number().int().min(0),
});
/**
 * What a guardian gets asked at a parents' evening: how much, how often, what
 * kind of work, and is it going well. Derived entirely from assignments and
 * reviews, so nothing here can drift from the ledger.
 */
export const teenInsightsSchema = z.object({
    teenId: z.string(),
    teenName: z.string(),
    currency: currencyCodeSchema,
    totalEarnedMinor: minorAmountSchema,
    jobsCompleted: z.number().int().min(0),
    /** Average across every review written about them. Null when unrated. */
    averageRating: z.number().min(0).max(5).nullable(),
    reviewCount: z.number().int().min(0),
    /** Distinct employers they have worked for — breadth, not just volume. */
    employersWorkedFor: z.number().int().min(0),
    /** Most-worked categories first. */
    topCategories: z.array(z.object({ category: z.string(), jobs: z.number().int().min(0) })),
    /** Oldest month first, so a chart reads left to right. */
    months: z.array(monthlyActivitySchema),
});
export const insightsResponseSchema = z.object({
    teens: z.array(teenInsightsSchema),
});
//# sourceMappingURL=payments.js.map